import React, { useEffect, useMemo, useState } from 'react';
import { fileHubApi } from './api';
import type {
  BatchUploadPayload,
  CreateVersionPayload,
  DuplicateCheckItemInput,
  FileAuditLog,
  FileDetail,
  FileHubSummary,
  FileHubTab,
  FileItem,
  FileListResponse,
  SharedArticleItem,
} from './types';
import { QuickAccessSection } from './components/QuickAccessSection';
import { DocumentLibraryView } from './components/DocumentLibraryView';
import { MultiFileUploadView } from './components/MultiFileUploadView';
import { VersionsArchiveView } from './components/VersionsArchiveView';
import { SharedArticlesFileView } from './components/SharedArticlesFileView';
import { AiCitationsView } from './components/AiCitationsView';
import { AuditLogView } from './components/AuditLogView';
import { InlineViewerModal } from './components/InlineViewerModal';
import { NewVersionModal } from './components/NewVersionModal';
import { AclManageModal } from './components/AclManageModal';
import './file-storage.css';
import './file-hub.css';

interface Props {
  onClose?: () => void;
  onBack?: () => void;
}

type StorageView = FileHubTab | 'hub' | 'security';

const TITLE_BY_VIEW: Record<StorageView, string> = {
  hub: 'Файлы и хранилище',
  library: 'Файлы и хранилище',
  batch_upload: 'Многофайловые вложения',
  versions_archive: 'Версии и архив',
  security: 'Хранение и доступ',
  shared_articles: 'Совместные статьи',
  ai_citations: 'Консультант и цитирование',
  audit: 'Журнал аудита',
};

const BREADCRUMB_BY_VIEW: Record<StorageView, string> = {
  hub: 'Центр',
  library: 'Библиотека',
  batch_upload: 'Многофайловые вложения',
  versions_archive: 'Версии и архив',
  security: 'Хранение и доступ',
  shared_articles: 'Совместные статьи',
  ai_citations: 'Консультант и цитирование',
  audit: 'Журнал аудита',
};

/**
 * Document storage hub — port of template files-storage layer (hub / library / menu).
 * Styles: file-storage.css
 */
export const FileHubPage: React.FC<Props> = ({ onClose, onBack }) => {
  const [activeTab, setActiveTab] = useState<StorageView>('hub');
  const [summary, setSummary] = useState<FileHubSummary | null>(null);
  const [fileList, setFileList] = useState<FileListResponse | null>(null);
  const [sharedArticles, setSharedArticles] = useState<SharedArticleItem[]>([]);
  const [auditLogs, setAuditLogs] = useState<FileAuditLog[]>([]);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [aiOnlyFilter, setAiOnlyFilter] = useState(false);
  const [sortBy, setSortBy] = useState('updated_at');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [viewerFile, setViewerFile] = useState<FileDetail | null>(null);
  const [viewerAnchor, setViewerAnchor] = useState<string | null>(null);
  const [versionTargetFile, setVersionTargetFile] = useState<FileItem | null>(null);
  const [aclTargetFile, setAclTargetFile] = useState<FileDetail | null>(null);
  const [selectedVersionFileDetail, setSelectedVersionFileDetail] = useState<FileDetail | null>(null);

  const handleBack = onBack ?? onClose ?? (() => undefined);
  const handleClose = onClose ?? onBack ?? (() => undefined);

  const loadSummary = async () => {
    try {
      const data = await fileHubApi.getSummary();
      setSummary(data);
    } catch {
      setSummary(null);
    }
  };

  const loadFiles = async () => {
    try {
      const res = await fileHubApi.listFiles({
        category: selectedCategory,
        search: searchQuery,
        file_type: fileTypeFilter,
        status: statusFilter,
        ai_only: aiOnlyFilter,
        sort_by: sortBy,
        page,
        page_size: pageSize,
      });
      setFileList(res);
    } catch {
      setFileList(null);
    }
  };

  const loadSharedArticles = async () => {
    try {
      const res = await fileHubApi.getSharedArticles();
      setSharedArticles(res.items);
    } catch {
      setSharedArticles([]);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const res = await fileHubApi.listAudit();
      setAuditLogs(res.items);
    } catch {
      setAuditLogs([]);
    }
  };

  useEffect(() => {
    loadSummary();
    loadFiles();
    loadSharedArticles();
    loadAuditLogs();
  }, []);

  useEffect(() => {
    loadFiles();
  }, [selectedCategory, searchQuery, fileTypeFilter, statusFilter, aiOnlyFilter, sortBy, page]);

  const handleOpenPreview = async (fileId: string, anchor?: string) => {
    try {
      const detail = await fileHubApi.getFileDetail(fileId);
      setViewerFile(detail);
      setViewerAnchor(anchor || null);
    } catch {
      setViewerFile(null);
    }
  };

  const handleDownload = async (fileId: string) => {
    try {
      const res = await fileHubApi.downloadFile(fileId);
      if (res?.download_url) {
        const blob = await fileHubApi.downloadFileStream(fileId, res.version_id);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = res.original_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      loadAuditLogs();
    } catch {
      alert('Ошибка при скачивании файла или ограничение прав доступа.');
    }
  };

  const buildFileHubLink = (fileId: string, versionId?: string | null) => {
    const v = versionId || '';
    return `${window.location.origin}/app/files/${fileId}${v ? `?v=${encodeURIComponent(v)}` : ''}`;
  };

  const handleCopyLink = (file: FileItem | FileDetail) => {
    const url = buildFileHubLink(file.file_id, file.current_version_id || file.current_version_no);
    void navigator.clipboard.writeText(url);
  };

  const handleToggleAi = async (fileId: string, currentEnabled: boolean) => {
    try {
      await fileHubApi.toggleAiSource(fileId, {
        ai_enabled: !currentEnabled,
        priority: 60,
      });
      loadFiles();
      loadSummary();
      loadAuditLogs();
    } catch {
      alert('Ошибка при переключении статуса источника AI.');
    }
  };

  const handleArchiveToggle = async (fileId: string) => {
    try {
      await fileHubApi.archiveFile(fileId);
      loadFiles();
      loadSummary();
      loadAuditLogs();
      if (selectedVersionFileDetail?.file_id === fileId) {
        const updated = await fileHubApi.getFileDetail(fileId);
        setSelectedVersionFileDetail(updated);
      }
    } catch {
      alert('Ошибка при архивации/восстановлении файла.');
    }
  };

  const handleBatchUpload = async (payload: BatchUploadPayload) => {
    await fileHubApi.uploadBatch(payload);
    loadFiles();
    loadSummary();
    loadAuditLogs();
  };

  const handleCheckDuplicates = async (items: DuplicateCheckItemInput[]) => {
    return fileHubApi.checkDuplicates(items);
  };

  const handleSelectVersionFile = async (fileId: string) => {
    try {
      const detail = await fileHubApi.getFileDetail(fileId);
      setSelectedVersionFileDetail(detail);
    } catch {
      setSelectedVersionFileDetail(null);
    }
  };

  const handleRestoreVersion = async (fileId: string, versionId: string) => {
    try {
      await fileHubApi.restoreVersion(fileId, versionId);
      const detail = await fileHubApi.getFileDetail(fileId);
      setSelectedVersionFileDetail(detail);
      loadFiles();
      loadSummary();
      loadAuditLogs();
    } catch {
      alert('Ошибка при восстановлении версии.');
    }
  };

  const handleCreateVersionSubmit = async (payload: CreateVersionPayload) => {
    if (!versionTargetFile) return;
    await fileHubApi.createVersion(versionTargetFile.file_id, payload);
    loadFiles();
    loadSummary();
    loadAuditLogs();
  };

  const handleManageAclOpen = async (file: FileItem) => {
    try {
      const detail = await fileHubApi.getFileDetail(file.file_id);
      setAclTargetFile(detail);
    } catch {
      setAclTargetFile(null);
    }
  };

  const handleSaveAcl = async (
    acls: Array<{ scope_type: string; scope_id: string; permissions: string }>
  ) => {
    if (!aclTargetFile) return;
    await fileHubApi.updateAcl(aclTargetFile.file_id, acls);
    loadFiles();
    loadAuditLogs();
  };

  const go = (view: StorageView) => {
    setActiveTab(view);
    if (view === 'versions_archive' && fileList?.items?.length && !selectedVersionFileDetail) {
      void handleSelectVersionFile(fileList.items[0].file_id);
    }
  };

  const showQuickAccess = activeTab === 'hub';

  const libraryFilterLabel = useMemo(() => {
    if (statusFilter === 'template') return 'Шаблоны';
    if (statusFilter === 'private') return 'Закрытые';
    return 'Все';
  }, [statusFilter]);

  return (
    <div
      className="file-storage-overlay open"
      role="dialog"
      aria-modal="true"
      aria-label="Файлы и хранилище"
      data-testid="file-hub-page"
    >
      <header className="file-storage-top">
        <div>
          <button
            className="file-storage-btn ghost"
            type="button"
            onClick={() => {
              if (viewerFile) {
                setViewerFile(null);
                setViewerAnchor(null);
                return;
              }
              handleBack();
            }}
          >
            ← Назад
          </button>
        </div>
        <div>
          <h2>{viewerFile ? 'Встроенный просмотр' : TITLE_BY_VIEW[activeTab]}</h2>
          <small>Владелец системы · Zertteu Academy</small>
        </div>
        <div className="file-storage-top-right">
          {!viewerFile ? (
            <button className="file-storage-btn" type="button" onClick={() => go('hub')}>
              Центр
            </button>
          ) : null}
          <button className="file-storage-btn danger" type="button" onClick={handleClose}>
            Закрыть ×
          </button>
        </div>
      </header>

      <main className="file-storage-main">
        {viewerFile ? (
          <div className="file-storage-wrap">
            <InlineViewerModal
              file={viewerFile}
              highlightAnchor={viewerAnchor}
              onClose={() => {
                setViewerFile(null);
                setViewerAnchor(null);
              }}
              onDownload={handleDownload}
              onCopyLink={handleCopyLink}
              onOpenVersions={() => {
                setViewerFile(null);
                setViewerAnchor(null);
                go('versions_archive');
                void handleSelectVersionFile(viewerFile.file_id);
              }}
            />
          </div>
        ) : (
        <div className="file-storage-wrap">
          <div className="file-storage-breadcrumb">
            Файлы и хранилище · <b>{BREADCRUMB_BY_VIEW[activeTab]}</b>
          </div>

          {(activeTab === 'hub' || activeTab === 'library') && (
            <div className="file-storage-hero">
              <div>
                <h1>Файлы, просмотр и версии</h1>
                <p>
                  Единый безопасный контур хранения, объектных прав доступа, просмотра в браузере,
                  многофайловых вложений и версионной библиотеки. Внешние диски остаются только
                  справочным источником и не используются как основное хранилище приватных файлов.
                </p>
              </div>
              <span className="file-storage-role-pill">Владелец системы</span>
            </div>
          )}

          <div className="file-storage-menu" aria-label="Разделы хранилища">
            <button
              type="button"
              aria-label="Библиотека документов"
              style={{ ['--file-storage-accent' as string]: '#41d2ff' }}
              className={activeTab === 'library' ? 'is-active' : undefined}
              onClick={() => go('library')}
            >
              <span className="ic" aria-hidden="true">
                ▤
              </span>
              <strong>Библиотека документов</strong>
              <span>Открыть PDF и DOCX без обязательного скачивания.</span>
            </button>
            <button
              type="button"
              aria-label="Многофайловые вложения"
              style={{ ['--file-storage-accent' as string]: '#65dda7' }}
              className={activeTab === 'batch_upload' ? 'is-active' : undefined}
              onClick={() => go('batch_upload')}
            >
              <span className="ic" aria-hidden="true">
                ＋
              </span>
              <strong>Многофайловые вложения</strong>
              <span>Word, Excel, CSV, PDF, изображения, аудио и другие разрешённые типы.</span>
            </button>
            <button
              type="button"
              aria-label="Версии и архив"
              style={{ ['--file-storage-accent' as string]: '#e2ad3a' }}
              className={activeTab === 'versions_archive' ? 'is-active' : undefined}
              onClick={() => go('versions_archive')}
            >
              <span className="ic" aria-hidden="true">
                ↺
              </span>
              <strong>Версии и архив</strong>
              <span>Текущая версия, дата действия, источник, архив и заметка об изменении.</span>
            </button>
            <button
              type="button"
              aria-label="Хранение и доступ"
              style={{ ['--file-storage-accent' as string]: '#ff9b77' }}
              className={activeTab === 'security' || activeTab === 'audit' ? 'is-active' : undefined}
              onClick={() => go('security')}
            >
              <span className="ic" aria-hidden="true">
                ⌾
              </span>
              <strong>Хранение и доступ</strong>
              <span>Объектное хранилище, метаданные, шифрование, права доступа и журнал.</span>
            </button>
            <button
              type="button"
              aria-label={`Совместные статьи (${sharedArticles.length})`}
              style={{ ['--file-storage-accent' as string]: '#b399ff' }}
              className={activeTab === 'shared_articles' ? 'is-active' : undefined}
              onClick={() => go('shared_articles')}
            >
              <span className="ic" aria-hidden="true">
                ✎
              </span>
              <strong>Совместные статьи</strong>
              <span>Общая рабочая карточка владельца и научного партнёра.</span>
            </button>
            <button
              type="button"
              aria-label="AI-источники и цитирование"
              style={{ ['--file-storage-accent' as string]: '#7ec8ff' }}
              className={activeTab === 'ai_citations' ? 'is-active' : undefined}
              onClick={() => go('ai_citations')}
            >
              <span className="ic" aria-hidden="true">
                ◈
              </span>
              <strong>AI-источники и цитирование</strong>
              <span>Проверяемые ответы с привязкой к первоисточникам.</span>
            </button>
          </div>

          {showQuickAccess && summary && summary.quick_access.length > 0 && (
            <QuickAccessSection
              items={summary.quick_access}
              onOpenPreview={handleOpenPreview}
              onDownload={handleDownload}
            />
          )}

          {activeTab === 'library' && (
            <>
              <div className="file-storage-section-title">
                <div>
                  <h3>Актуальные файлы и шаблоны</h3>
                  <p>Основное действие — «Открыть / Посмотреть». Дополнительно — «Скачать».</p>
                </div>
                <div className="file-storage-tools">
                  <input
                    className="file-storage-search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Поиск по файлам…"
                    aria-label="Поиск по файлам"
                  />
                  <button
                    type="button"
                    className="file-storage-btn"
                    onClick={() => setStatusFilter('all')}
                  >
                    Все
                  </button>
                  <button
                    type="button"
                    className="file-storage-btn"
                    onClick={() => setStatusFilter('template')}
                  >
                    Шаблоны
                  </button>
                  <button
                    type="button"
                    className="file-storage-btn"
                    onClick={() => setStatusFilter('private')}
                  >
                    Закрытые
                  </button>
                  <span className="file-storage-chip current" style={{ alignSelf: 'center' }}>
                    {libraryFilterLabel}
                  </span>
                </div>
              </div>
              {fileList && (
                <DocumentLibraryView
                  files={fileList.items}
                  categories={fileList.categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={(cat) => {
                    setSelectedCategory(cat);
                    setPage(1);
                  }}
                  searchQuery={searchQuery}
                  onSearchChange={(query) => {
                    setSearchQuery(query);
                    setPage(1);
                  }}
                  fileTypeFilter={fileTypeFilter}
                  onFileTypeChange={(type) => {
                    setFileTypeFilter(type);
                    setPage(1);
                  }}
                  statusFilter={statusFilter}
                  onStatusChange={(status) => {
                    setStatusFilter(status);
                    setPage(1);
                  }}
                  aiOnlyFilter={aiOnlyFilter}
                  onAiOnlyChange={(enabled) => {
                    setAiOnlyFilter(enabled);
                    setPage(1);
                  }}
                  sortBy={sortBy}
                  onSortChange={(sort) => {
                    setSortBy(sort);
                    setPage(1);
                  }}
                  page={page}
                  pageSize={pageSize}
                  total={fileList.total}
                  onPageChange={setPage}
                  onOpenPreview={handleOpenPreview}
                  onDownload={handleDownload}
                  onNewVersionClick={(file) => setVersionTargetFile(file)}
                  onManageAclClick={handleManageAclOpen}
                  onToggleAi={handleToggleAi}
                  onArchiveToggle={handleArchiveToggle}
                  onCopyLink={handleCopyLink}
                />
              )}
            </>
          )}

          {activeTab === 'batch_upload' && (
            <MultiFileUploadView
              onUploadSubmit={handleBatchUpload}
              onCheckDuplicates={handleCheckDuplicates}
            />
          )}

          {activeTab === 'versions_archive' && fileList && (
            <VersionsArchiveView
              files={fileList.items}
              selectedFileDetail={selectedVersionFileDetail}
              onSelectFile={handleSelectVersionFile}
              onRestoreVersion={handleRestoreVersion}
              onArchiveToggle={handleArchiveToggle}
              onOpenPreview={handleOpenPreview}
            />
          )}

          {activeTab === 'shared_articles' && (
            <SharedArticlesFileView
              articles={sharedArticles}
              onOpenPreview={handleOpenPreview}
              onDownload={handleDownload}
            />
          )}

          {activeTab === 'ai_citations' && (
            <AiCitationsView
              onRunAiQuery={(q, r) => fileHubApi.queryAi(q, r)}
              onOpenPreviewAtAnchor={handleOpenPreview}
            />
          )}

          {activeTab === 'security' && (
            <div className="file-storage-sec-grid">
              <article className="file-storage-panel">
                <h4>Хранение и доступ</h4>
                <p>
                  Доступ к файлам зависит от ваших прав. Историю работы с документом можно посмотреть в журнале действий.
                </p>
                <div className="file-storage-flow">
                  <span>Загрузка</span>
                  <i>→</i>
                  <span>Проверка доступа</span>
                  <i>→</i>
                  <span>Файлы</span>
                  <i>+</i>
                  <span>Сведения о документах</span>
                  <i>+</i>
                  <span>История действий</span>
                </div>
              </article>
              <article className="file-storage-panel">
                <h4>Секреты и пароли</h4>
                <p>
                  Не добавляйте пароли и коды входа в общие документы. Перед передачей файла проверьте его содержимое и список получателей.
                </p>
              </article>
              <article className="file-storage-panel">
                <h4>Журнал аудита</h4>
                <p>
                  Просмотр, скачивание, смена версии, изменение прав и загрузка фиксируются как
                  отдельные события. Откройте полный журнал ниже.
                </p>
                <button
                  type="button"
                  className="file-storage-btn primary"
                  style={{ marginTop: 12 }}
                  onClick={() => go('audit')}
                >
                  Открыть журнал ({auditLogs.length})
                </button>
              </article>
            </div>
          )}

          {activeTab === 'audit' && <AuditLogView logs={auditLogs} onRefresh={loadAuditLogs} />}
        </div>
        )}
      </main>

      {versionTargetFile && (
        <NewVersionModal
          file={versionTargetFile}
          onClose={() => setVersionTargetFile(null)}
          onSubmit={handleCreateVersionSubmit}
        />
      )}

      {aclTargetFile && (
        <AclManageModal
          file={aclTargetFile}
          onClose={() => setAclTargetFile(null)}
          onSaveAcl={handleSaveAcl}
        />
      )}
    </div>
  );
};
